// Specimen report panel (A4, monorail-a3f) — renders buildStatsModel().
// Toggled with I; freezes the sim via the same gate contract as the shop.
// A5 (monorail-skv): the wardrobe lives here — per-slot collection lists,
// click to equip, stats above update in place (swap a hat, watch Guard move).

import type { Entity, ItemSlot } from '../game/components'
import { buildStatsModel } from '../game/stats'
import { equipItem, type CompareLine } from '../game/items'
import { WEAPON_DEFS } from '../game/weapons'

export interface StatsCard {
  isOpen(): boolean
  toggle(player: Entity): void
  close(): void
}

const SLOT_HEADER: Record<ItemSlot, string> = { hat: 'Hats', weapon: 'Weapons' }

export function createStatsCard(onGearChange?: () => void): StatsCard {
  const overlay = document.getElementById('stats-card') as HTMLDivElement
  const body = document.getElementById('stats-card-body') as HTMLDivElement
  const closeBtn = document.getElementById('stats-card-close') as HTMLButtonElement
  let open = false

  function render(player: Entity): void {
    const model = buildStatsModel(player)
    body.innerHTML = ''

    // The four stats
    const statList = document.createElement('div')
    statList.className = 'stats-rows'
    for (const row of model.stats) {
      const el = document.createElement('div')
      el.className = 'stats-row'
      const head = document.createElement('div')
      head.className = 'stats-row-head'
      head.innerHTML = `<span class="stats-label">${row.label}</span><span class="stats-value">${row.value}</span>`
      el.appendChild(head)
      if (row.sources.length > 0) {
        const src = document.createElement('div')
        src.className = 'stats-sources'
        src.textContent = row.sources.join(' · ')
        el.appendChild(src)
      }
      statList.appendChild(el)
    }
    body.appendChild(statList)

    // Wardrobe (A5): per-slot lists, click to equip. The bare row unequips —
    // hats to nothing, weapons back to the innate pseudopod. Non-worn rows
    // carry green/red per-stat deltas against the worn item.
    for (const slot of ['hat', 'weapon'] as ItemSlot[]) {
      body.appendChild(sectionHeader(SLOT_HEADER[slot]))
      const entries = model.wardrobe[slot]
      const nothingWorn = !entries.some(e => e.equipped)
      const bare = slot === 'weapon'
        ? wardrobeRow(WEAPON_DEFS['pseudopod'].name, 'innate', [], nothingWorn)
        : wardrobeRow('Nothing. Noted.', null, [], nothingWorn)
      bare.addEventListener('click', () => {
        equipItem(player, slot, null)
        onEquipChanged(player)
      })
      body.appendChild(bare)
      for (const entry of entries) {
        const row = wardrobeRow(entry.name, entry.rarity, entry.lines, entry.equipped)
        row.addEventListener('click', () => {
          equipItem(player, slot, entry.id)
          onEquipChanged(player)
        })
        body.appendChild(row)
      }
    }
  }

  function sectionHeader(text: string): HTMLDivElement {
    const el = document.createElement('div')
    el.className = 'stats-section'
    el.textContent = text
    return el
  }

  function wardrobeRow(name: string, rarity: string | null, lines: CompareLine[], worn: boolean): HTMLDivElement {
    const el = document.createElement('div')
    el.className = 'wardrobe-row' + (worn ? ' worn' : '')
    const star = rarity === 'pristine' ? ' ★' : ''
    const nameCls = rarity ? `stats-item-name rarity-${rarity}` : 'stats-item-name stats-empty'
    el.innerHTML =
      `<span class="${nameCls}">${name}${star}</span>` +
      (worn ? '<span class="wardrobe-worn-tag">worn</span>' : '')
    if (lines.length > 0) el.appendChild(affixGrid(lines, !worn))
    return el
  }

  function onEquipChanged(player: Entity): void {
    render(player) // stats above recompute in place — the swap payoff
    onGearChange?.()
  }

  const card: StatsCard = {
    isOpen: () => open,
    toggle(player: Entity) {
      open = !open
      if (open) render(player)
      overlay.classList.toggle('visible', open)
    },
    close() {
      open = false
      overlay.classList.remove('visible')
    },
  }
  closeBtn.addEventListener('click', () => card.close())
  return card
}

/** Aligned affix rows: label | value | delta. Same columns on every item so
 * comparisons scan vertically instead of re-reading prose. Shared with the
 * pickup card. */
export function affixGrid(lines: CompareLine[], showDeltas: boolean): HTMLDivElement {
  const grid = document.createElement('div')
  grid.className = 'affix-grid'
  for (const line of lines) {
    const label = document.createElement('span')
    label.className = 'affix-label'
    label.textContent = line.label
    const value = document.createElement('span')
    value.className = 'affix-value' + (line.value === '—' ? ' affix-missing' : '')
    value.textContent = line.value
    const delta = document.createElement('span')
    delta.className = 'affix-delta'
    if (showDeltas && line.deltaText) {
      delta.textContent = line.deltaText
      delta.classList.add(line.delta > 0 ? 'affix-up' : 'affix-down')
    }
    grid.append(label, value, delta)
  }
  return grid
}
